/*
 * Modules dependencies
 */

import { Button } from 'src/components/core/buttons/button';
import { Pricing } from 'src/components/core/pricing';
import { RawHtml } from '@untile/react-core/components/raw-html';
import { Text } from 'src/components/core/text';
import { Toggle } from 'src/components/core/toggle';
import { media } from '@untile/react-core/styles/media';
import { transparentize } from 'src/styles/utils/colors';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useTranslation } from 'next-i18next';
import camelCase from 'lodash/camelCase';
import downloadIcon from 'src/assets/svgs/download.svg';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  description?: string;
  descriptionDetailPage?: string;
  descriptionTwelveMonths?: string;
  hideDescription?: boolean;
  hideServiceHeader?: boolean;
  hideServiceInfo?: boolean;
  label?: string;
  pdfUrl?: string;
  price?: number;
  priceTwelveMonths?: number;
  title?: string;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  background: ${transparentize('cyan800', 0.16)};
  border-radius: var(--border-radius-xs);
  display: grid;
  grid-template-columns: 1fr max-content;
  height: 100%;
  padding: var(--space-xxs) var(--space-xs);

  ${media.min.md`
    padding: var(--space-xs);
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Text).attrs({ as: 'h4', variant: 'labelM', weight: 600 })`
  background: var(--gradient-primary--15-deg);
  -webkit-background-clip: text;
  background-clip: text;
  color: var(--color-primary);
  padding: var(--space-xxs) 0;
  -webkit-text-fill-color: transparent;
  text-transform: uppercase;
`;

/*
 * `Description` styled component.
 */

const Description = styled(Text).attrs({ as: 'p', variant: 'bodyM' })`
  padding: var(--space-xxs) 0;
`;

/**
 * `Label` styled component.
 */

const Label = styled(Text).attrs({ variant: 'labelS' })`
  background-color: ${transparentize('cyan400', 0.5)};
  border-radius: var(--border-radius-xxs);
  margin-left: var(--space-xxs);
  padding: 3px var(--space-xxs);
  white-space: nowrap;
`;

/**
 * `PricingWrapper` styled component.
 */

const PricingWrapper = styled.div`
  margin-left: var(--space-xs);

  ${media.min.md`
    margin-left: calc(var(--space-ms) + var(--space-xxs));
  `}
`;

/**
 * Export `CardPricing` component.
 */

export function CardPricing(props: Props) {
  const {
    description,
    descriptionDetailPage,
    descriptionTwelveMonths,
    hideDescription,
    hideServiceHeader,
    hideServiceInfo,
    label,
    pdfUrl,
    price,
    priceTwelveMonths,
    title
  } = props;

  const { t } = useTranslation('common');
  const [activeTab, setActiveTab] = useState<'left' | 'right'>('left');
  const { pathname } = useRouter();

  const serviceDescription = hideDescription ? descriptionDetailPage : description;

  return (
    <Wrapper>
      <div>
        {!hideServiceInfo && !hideServiceHeader && (
          <div style={{ alignItems: 'center', display: 'flex' }}>
            {title && <Title>{title}</Title>}

            {label && <Label>{label}</Label>}
          </div>
        )}

        {price && priceTwelveMonths && (
          <div style={{ padding: 'var(--space-xxs) 0' }}>
            <Toggle
              activeTab={activeTab}
              handleActiveTab={() => setActiveTab(activeTab === 'left' ? 'right' : 'left')}
              id={`${pathname}-${camelCase(title)}`}
              labelLeft={t('common:labels.sixMonths')}
              labelRight={t('common:labels.twelveMonths')}
            />
          </div>
        )}

        {serviceDescription && !hideServiceInfo && (
          <RawHtml element={Description}>
            {activeTab === 'left' ? serviceDescription : descriptionTwelveMonths ?? serviceDescription}
          </RawHtml>
        )}

        {pdfUrl && !hideServiceInfo && (
          <Button
            aria-label={t('common:actions.checkTransportPrices')}
            colorTheme={'text'}
            href={pdfUrl}
            icon={downloadIcon}
            reverse
            target={'_blank'}
          >
            {t('common:actions.checkTransportPrices')}
          </Button>
        )}
      </div>

      {price && (
        <PricingWrapper>
          <Pricing price={activeTab === 'left' ? price : priceTwelveMonths ?? price} />
        </PricingWrapper>
      )}
    </Wrapper>
  );
}
